import { Button, ClassCard } from '@wot/components';
import { ISessionAttendance, ITrialClass } from '@wot/typings';
import { parseISO } from 'date-fns';
import { format } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DEFAULT_PROFILE_PIC } from '../../../assets/icons';
import { Portal } from '../../../components/Modal';
import xhr from '../../../utils/fetch';
import { useQueryClient } from '@tanstack/react-query';

export default function Dispute() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<ITrialClass>();
  const navigate = useNavigate();
  const { session_id } = useParams();
  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!session_id) {
      return;
    }
    xhr({
      url: `/a/classes/${session_id}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setSession(res.data);
        }
      })
      .catch((err) => err);
  }, []);

  if (!session?.dispute || !session) {
    return null;
  }

  const markStudentAttendance = (attendance: ISessionAttendance) => {
    xhr({
      url: `/u/${session.userId}/sessions/${session._id}`,
      method: 'PUT',
      body: {
        status: 'completed',
        attendance: {
          ...session.attendance,
          student: attendance
        },
        actionRequired: session.attendance.tutor !== 'pending' ? '' : session.actionRequired
      }
    })
      .then((res) => {
        if (res.status === 'OK') {
          setSession({
            ...session,
            attendance: {
              ...session.attendance,
              student: attendance
            },
            actionRequired: session.attendance.tutor !== 'pending' ? undefined : session.actionRequired
          });
        }
      })
      .catch((err) => err);
  };

  const markTutorAttendance = (attendance: ISessionAttendance) => {
    xhr({
      url: `/u/${session.userId}/sessions/${session._id}`,
      method: 'PUT',
      body: {
        status: 'completed',
        attendance: {
          ...session.attendance,
          tutor: attendance
        },
        actionRequired: session.attendance.student !== 'pending' ? '' : session.actionRequired
      }
    })
      .then((res) => {
        if (res.status === 'OK') {
          setSession({
            ...session,
            attendance: {
              ...session.attendance,
              tutor: attendance
            },
            actionRequired: undefined
          });
        }
      })
      .catch((err) => err);
  };

  const updateDisputeStatus = (status: 'active' | 'resolved') => {
    xhr({
      url: `/u/${session.userId}/sessions/${session._id}`,
      method: 'PUT',
      body: {
        status: 'completed',
        dispute: {
          ...session.dispute,
          status: status
        }
      }
    })
      .then((res) => {
        if (res.status === 'OK') {
          toast.success(`Dispute Resolved for Class Id: ${session._id}`);
          queryClient.invalidateQueries({ queryKey: ['classes'] });
          navigate(-1);
        }
      })
      .catch((err) => err);
  };

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={() => handleClose()} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"></div>
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl rounded-bl-2xl p-7 overflow-y-scroll">
            <div className="w-full flex flex-col gap-2">
              <div className="w-full flex items-center justify-between">
                <h1 className="text-lg font-semibold">Dispute {session?._id}</h1>
                <MdClose className="text-2xl cursor-pointer" onClick={() => handleClose()} />
              </div>

              <div className="flex flex-col">
                <p className="text-sm">Date</p>
                <p className="text-sm font-medium">{format(parseISO(session?.dispute?.createdAt), 'd LLL, yyyy')}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Class ID</p>
                <p className="text-sm font-medium">{session._id}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Subject</p>
                <p className="text-sm font-medium">{session.dispute.subject}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Raised By</p>
                <p className="text-sm font-medium">
                  {session.dispute.raisedBy === 'user' ? session.user.name : session.tutor.name}
                </p>
              </div>
            </div>

            <div className="w-full flex gap-5">
              <div className="w-full flex flex-col gap-2">
                <h3 className="text-sm font-medium">Student:</h3>
                <div className="w-full max-w-[180px] flex flex-col gap-4 items-center justify-center p-7 border border-primary rounded-md">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img src={session.user.profilePic || DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
                  </div>
                  <h2 className="text-base font-medium">{session.user.name}</h2>
                  <Link to={`/student-management/${session.user._id}`}>
                    <Button type="secondary" text="View Profile" block onClick={() => {}} />
                  </Link>
                </div>
              </div>
              <div className="w-full flex flex-col gap-2">
                <h3 className="text-sm font-medium">Tutor:</h3>
                <div className="w-full max-w-[180px] flex flex-col gap-4 items-center justify-center p-7 border border-primary rounded-md">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img src={session.tutor.profilePic || DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
                  </div>
                  <h2 className="text-base font-medium">{session.tutor.name}</h2>
                  <Link to={`/tutor-management/manage/${session.tutor._id}`}>
                    <Button type="secondary" text="View Profile" block onClick={() => {}} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="w-full flex flex-col gap-4 p-4 border border-primary rounded-md">
              <p className="text-sm font-medium uppercase text-primary">Comment</p>
              <p className="text-sm">{session.dispute.description}</p>
            </div>

            <div className="w-full flex flex-col gap-4">
              <h3 className="text-sm font-medium">Student Attendace:</h3>
              <ClassCard
                _id={session._id}
                type={session.type}
                category={session.category}
                endTime={new Date(session.scheduledTime.end)}
                startTime={new Date(session.scheduledTime.start)}
                name={session.tutor?.name || ''}
                profilePic={session.tutor?.profilePic || DEFAULT_PROFILE_PIC}
                status={session.status}
                showJoinBtn={false}
                showCancelBtn={false}
                showRescheduleBtn={false}
                markSelfAttendance={session.dispute.status === 'active'}
                attendance={session.attendance && session.attendance.student}
                onMarkSelfAttendance={(value) => markStudentAttendance(value)}
              />
            </div>

            <div className="w-full flex flex-col gap-4">
              <h3 className="text-sm font-medium">Tutor Attendace:</h3>
              <ClassCard
                _id={session._id}
                type={session.type}
                category={session.category}
                endTime={new Date(session.scheduledTime.end)}
                startTime={new Date(session.scheduledTime.start)}
                name={session.user?.name || ''}
                profilePic={session.user?.profilePic || DEFAULT_PROFILE_PIC}
                status={session.status}
                showJoinBtn={false}
                showCancelBtn={false}
                showRescheduleBtn={false}
                markSelfAttendance={session.dispute.status === 'active'}
                attendance={session.attendance && session.attendance.tutor}
                onMarkSelfAttendance={(value) => markTutorAttendance(value)}
              />
            </div>

            <div className="w-full flex items-center gap-5">
              <Button type="s-outlined" block text="Cancel" onClick={() => navigate(-1)} />
              {session.dispute.status === 'active' ? (
                <Button block text="Mark as Resolved" onClick={() => updateDisputeStatus('resolved')} />
              ) : (
                <Button block text="Mark as Active" onClick={() => updateDisputeStatus('active')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
